/**
 * Decide si retomar una conversación previa del estudiante o empezar una nueva.
 *
 * Regla:
 *  - Solo aplica para modos 'conversacional' y 'estudio' (examen y actividades-*
 *    se diseñaron como sesiones cerradas).
 *  - Se mira la última conversación del email en Supabase y se calcula la
 *    "última actividad" como el max(questions[*].ts), con fallback a `modified`
 *    y luego a `created`.
 *  - Si la última actividad fue hace < 24h Y el modo previo también es retomar-
 *    friendly, se devuelve la conversación para hidratar el cliente.
 *  - Cualquier otro caso (>24h, modo previo cerrado, sin historia) → null y
 *    el cliente arranca con conversación nueva como hasta ahora.
 *
 * Decisión de Laura (2026-05-06): no truncar el historial al retomar — el
 * contexto completo se manda al LLM. Aceptamos el costo/latencia mayor a
 * cambio de continuidad total.
 */
import supabaseService from './supabaseService';
import type { Conversation, ChatMessage } from '../types';

/** Modos donde retomar una conversación previa < 24h tiene sentido. */
const RESUMABLE_MODES = new Set(['conversacional', 'estudio']);

/** Ventana de inactividad: 24 horas en milisegundos. */
const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Guardián del botón "Nueva conversación" (pedido Diana Soler 2026-07-21):
 * cuando el estudiante reinicia explícitamente, las conversaciones anteriores
 * a ese momento dejan de ser retomables — si no, una recarga dentro de las
 * 24h rehidrataría justo el hilo que él pidió borrar.
 */
const RESET_MARKER_KEY = 'galia_resume_ignore_before';

export function markConversationReset(): void {
  try {
    localStorage.setItem(RESET_MARKER_KEY, String(Date.now()));
  } catch {
    // localStorage no disponible: se pierde el guardián, no la funcionalidad.
  }
}

function resetMarkerMs(): number {
  try {
    const v = Number(localStorage.getItem(RESET_MARKER_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

/** Estructura que un caller usa para hidratar el cliente. */
export interface ResumePayload {
  internalInteractionId: string;
  /** Mensajes en formato ChatMessage listos para inyectar en webSocketService. */
  messages: ChatMessage[];
  /** Cuántos mensajes ya están persistidos en Supabase (= length actual). */
  alreadyPersistedCount: number;
  /** Modo de la conversación que se está retomando. */
  mode: string;
  /** Fecha/ms de la última actividad detectada (para logging). */
  lastActivityAt: number;
  /**
   * time_total ya persistido en la fila (segundos de sesiones anteriores de
   * esta misma conversación). Se usa como baseline en conversationSaverService
   * para que el próximo upsert no lo sobrescriba con solo el tiempo de la
   * sesión actual.
   */
  previousTimeTotalSeconds: number;
}

/**
 * Devuelve el ts (epoch ms) más reciente del array de questions, o null si
 * ninguno tiene timestamp. La Lambda original guarda solo {role, content};
 * el frontend enriquece con `ts` y `receivedAt` después (ver
 * conversationSaverService.ts:1019). Por eso puede no estar siempre presente.
 */
function maxQuestionTimestamp(questions: unknown): number | null {
  if (!Array.isArray(questions) || questions.length === 0) return null;
  let max: number | null = null;
  for (const q of questions) {
    if (q && typeof q === 'object') {
      const ts = (q as { ts?: unknown }).ts;
      if (typeof ts === 'number' && Number.isFinite(ts)) {
        if (max === null || ts > max) max = ts;
      }
    }
  }
  return max;
}

function parseIsoToMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Calcula la última actividad de una conversación con la mejor señal disponible.
 * Prioridad: max(questions[*].ts) > modified > created.
 */
export function computeLastActivityMs(row: Conversation): number {
  const fromQuestions = maxQuestionTimestamp(row.questions);
  if (fromQuestions !== null) return fromQuestions;
  const fromModified = parseIsoToMs(row.modified);
  if (fromModified !== null) return fromModified;
  const fromCreated = parseIsoToMs(row.created);
  if (fromCreated !== null) return fromCreated;
  return 0;
}

/**
 * Convierte el array `questions` (formato Supabase {role, content[, ts, receivedAt]})
 * al formato ChatMessage que consume webSocketService.loadChatHistory().
 */
export function questionsToChatMessages(questions: unknown): ChatMessage[] {
  if (!Array.isArray(questions)) return [];
  const out: ChatMessage[] = [];
  for (const q of questions) {
    if (!q || typeof q !== 'object') continue;
    const obj = q as { role?: unknown; content?: unknown; ts?: unknown; receivedAt?: unknown };
    if ((obj.role !== 'user' && obj.role !== 'assistant') || typeof obj.content !== 'string') continue;
    const msg: ChatMessage = { role: obj.role, content: obj.content };
    if (typeof obj.ts === 'number') msg.timestamp = obj.ts;
    if (typeof obj.receivedAt === 'number') msg.receivedAt = obj.receivedAt;
    out.push(msg);
  }
  return out;
}

/**
 * Intenta resolver el retome. Si hay candidato válido, retorna ResumePayload;
 * si no, null y el caller arranca con conversación nueva.
 *
 * Errores de red/Supabase se silencian (return null) para fallar abierto: la
 * peor experiencia de un fallo aquí es perder la continuidad, no romper la app.
 */
export async function tryResumeConversation(
  email: string,
  currentMode: string | undefined,
): Promise<ResumePayload | null> {
  if (!email) return null;
  if (!currentMode || !RESUMABLE_MODES.has(currentMode)) return null;

  try {
    const recent = await supabaseService.getConversationsByUser(email, 1);
    if (!recent || recent.length === 0) return null;
    const row = recent[0];

    // El modo previo también debe ser retomar-friendly. Si el alumno acaba de
    // salir de un examen, no queremos prepender ese examen a una conversación
    // libre.
    if (!row.mode || !RESUMABLE_MODES.has(row.mode)) return null;

    const lastActivityAt = computeLastActivityMs(row);
    if (lastActivityAt === 0) return null;
    if (Date.now() - lastActivityAt > RESUME_WINDOW_MS) return null;
    // Conversaciones previas a un reinicio explícito no se retoman.
    if (lastActivityAt <= resetMarkerMs()) return null;

    const messages = questionsToChatMessages(row.questions);
    if (messages.length === 0) return null;

    const previousTimeTotalSeconds =
      typeof row.time_total === 'number' && Number.isFinite(row.time_total) && row.time_total > 0
        ? Math.round(row.time_total)
        : 0;

    return {
      internalInteractionId: row.internal_interaction_id,
      messages,
      alreadyPersistedCount: messages.length,
      mode: row.mode,
      lastActivityAt,
      previousTimeTotalSeconds,
    };
  } catch (err) {
    console.error('[Resume] Error al evaluar retome de conversación:', err);
    return null;
  }
}
